/**
 * Recursively merges multiple objects into a target object with special handling for arrays.
 * Arrays are concatenated rather than merged, and undefined values are skipped which makes it
 * function similarly to the lodash/merge package.
 *
 * Does not handle more complex types like Date objects or Symbols
 *
 * @param {any} target - The target object to merge into
 * @param {...any[]} sources - One or more source objects to merge from
 * @returns {any} The merged object
 *
 * @example
 * const obj1 = { a: [1, 2], b: { c: 3 } };
 * const obj2 = { a: [3, 4], b: { d: 4 } };
 * deepMerge(obj1, obj2);
 * // Result: { a: [1, 2, 3, 4], b: { c: 3, d: 4 } }
 * const obj1 = { a: 3, c: 2 };
 * const obj2 = { a: undefined, b: 3 };
 * deepMerge(obj1, obj2);
 * // Result: { a: 3, b: 3, c: 2 }
 */
export const deepMerge = function (target) {
  for (var _len = arguments.length, sources = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    sources[_key - 1] = arguments[_key];
  }
  if (!sources.length) return target;
  const source = sources.shift();
  if (source === undefined) {
    return deepMerge(target, ...sources);
  }
  if (typeof target !== 'object' || typeof source !== 'object') {
    return source;
  }

  // Special handling for arrays - concatenate them
  if (Array.isArray(target) && Array.isArray(source)) {
    return [...target, ...source];
  }
  for (const key in source) {
    if (source[key] === undefined) {
      continue;
    }
    if (typeof source[key] === 'object' && source[key] !== null) {
      if (!target[key]) {
        Object.assign(target, {
          [key]: Array.isArray(source[key]) ? [] : {}
        });
      }
      // If both are arrays, concatenate them
      if (Array.isArray(target[key]) && Array.isArray(source[key])) {
        target[key] = [...target[key], ...source[key]];
      } else {
        deepMerge(target[key], source[key]);
      }
    } else {
      Object.assign(target, {
        [key]: source[key]
      });
    }
  }
  return deepMerge(target, ...sources);
};